import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import EarningsByType from './EarningsByType'
import EarningsByCategory from './EarningsByCategory'
import EarningsByProfessional from './EarningsByProfessional'
import EarningsByPaymentMethod from './EarningsByPaymentMethod'
import EarningsByBillItem from './EarningsByBillItem'
import EarningsAndExpenses from './EarningsAndExpenses'
import BonusesAndDiscounts from './BonusesAndDiscounts'
import CreditsAndDebts from './CreditsAndDebts'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DashboardContainer, LoadingContainer, InfoText } from '../styles'
import { DateTime } from 'luxon'

export default function Earnings ({ month, year }) {
  const [loading, setLoading] = useState (true)
  const [earningsByType, setEarningsByType] = useState (null)
  const [earningsByProfessional, setEarningsByProfessional] = useState (null)
  const [earningsByCategory, setEarningsByCategory] = useState (null)
  const [earningsByPaymentMethod, setEarningsByPaymentMethod] = useState (null)
  const [earningsByBillItem, setEarningsByBillItem] = useState (null)
  const [earnings, setEarnings] = useState ([])
  const [expenses, setExpenses] = useState ([])
  const [credits, setCredits] = useState ([])
  const [debts, setDebts] = useState ([])
  const [bonuses, setBonuses] = useState ([])
  const [discounts, setDiscounts] = useState ([])
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [month, year])

  const getData = async () => {
    try {
      setLoading (true)
      const selectedDate = DateTime.local (year.value, month.value)
      const startDate = selectedDate.startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `business_id=${businessId}&start_date=${startDate}&end_date=${endDate}&type=all_earnings`

      const [
        { data: earnings },
        { data: expenses },
        { data: credits },
        { data: debts },
        { data: bonuses },
        { data: discounts },
        { data: earningsByTypeResponse },
        { data: earningsByProfessional },
        { data: earningsByPaymentMethod },
        { data: earningsByCategory }
      ] = await Promise.all ([
        cutileiApi.get (`/earnings?${filters}`, requestConfig),
        cutileiApi.get (`/expenses?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/credits?${filters}&customer_id=all_customers`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/debts?${filters}&customer_id=all_customers`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/credits?${filters}&professional_id=all_professionals`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/debts?${filters}&professional_id=all_professionals`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/earnings_by_type?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/earnings_by_professional?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/earnings_by_payment_method?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/earnings_by_category?${filters}`, requestConfig)
      ])

      setEarningsByType (earningsByTypeResponse.earningsByType)
      setEarningsByBillItem (earningsByTypeResponse.earningsByBillItem)
      setEarningsByProfessional (earningsByProfessional)
      setEarningsByPaymentMethod (earningsByPaymentMethod)
      setEarningsByCategory (earningsByCategory)
      setEarnings (earnings)
      setExpenses (expenses)
      setCredits (credits)
      setDebts (debts)
      setBonuses (bonuses)
      setDiscounts (discounts)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return loading ? (
    <LoadingContainer>
      <Loading/>
      <InfoText>Gerando gráficos...</InfoText>
    </LoadingContainer>
  ) : (
    <DashboardContainer>
      <EarningsByType data={earningsByType} month={month.value} year={year.value}/>
      <EarningsByProfessional data={earningsByProfessional} month={month.value} year={year.value}/>
      <EarningsByCategory data={earningsByCategory} month={month.value} year={year.value}/>
      <EarningsByPaymentMethod data={earningsByPaymentMethod} month={month.value} year={year.value}/>
      <EarningsByBillItem data={earningsByBillItem} month={month.value} year={year.value}/>
      <EarningsAndExpenses earnings={earnings} expenses={expenses} month={month.value} year={year.value}/>
      <BonusesAndDiscounts bonuses={bonuses} discounts={discounts} month={month.value} year={year.value}/>
      <CreditsAndDebts credits={credits} debts={debts} month={month.value} year={year.value}/>
    </DashboardContainer>
  )
}
